import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NewsArticle } from '@/types/news';

export function useInfiniteFeed(source: NewsArticle[], pageSize = 4) {
  const [page, setPage] = useState(1);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const visibleItems = useMemo(() => source.slice(0, page * pageSize), [page, pageSize, source]);
  const hasMore = visibleItems.length < source.length;

  useEffect(() => {
    setPage(1);
  }, [source]);

  const loadMore = useCallback(() => {
    setPage((current) => (current * pageSize < source.length ? current + 1 : current));
  }, [pageSize, source.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: '240px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  return { visibleItems, hasMore, sentinelRef, loadMore };
}
